import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, ImageOff } from 'lucide-react';
import { useRoomMedia, type ChatAttachmentMeta } from '../../lib/chat';
import { humanSize, isPdf } from './attachmentUtils';
import { FilePreviewModal } from './FilePreviewModal';

/**
 * "View media & docs" — every image, video and document shared in a room,
 * with previews. Clicking any item opens the full preview (image / video / PDF)
 * or a download for non-previewable files. Rendered in a portal so it works
 * identically from the Team Chat page and the Let's Chat bubble.
 */
export function ChatMediaGallery({
  roomPublicId,
  onClose,
}: {
  roomPublicId: string;
  onClose: () => void;
}) {
  const media = useRoomMedia(roomPublicId);
  const [preview, setPreview] = useState<ChatAttachmentMeta | null>(null);
  const items = media.data ?? [];
  const withMeta = items.filter((i): i is typeof i & { metadata: ChatAttachmentMeta } => !!i.metadata?.url);
  const visuals = withMeta.filter((i) => i.metadata.media === 'image' || i.metadata.media === 'video');
  const docs = withMeta.filter((i) => i.metadata.media === 'file');

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-neutral-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-neutral-800">Media &amp; docs</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {media.isLoading ? (
            <p className="py-8 text-center text-sm text-neutral-400">Loading…</p>
          ) : withMeta.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-neutral-400">
              <ImageOff className="size-8" />
              <p className="text-sm">No media or documents shared yet</p>
            </div>
          ) : (
            <div className="space-y-5">
              {visuals.length > 0 && (
                <section>
                  <h4 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-neutral-400">
                    Media ({visuals.length})
                  </h4>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {visuals.map((i) => (
                      <button
                        key={i.publicId}
                        type="button"
                        onClick={() => setPreview(i.metadata)}
                        className="group relative aspect-square overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50"
                        title={`${i.metadata.filename} · ${i.senderName}`}
                      >
                        {i.metadata.media === 'image' ? (
                          <img src={i.metadata.url} alt={i.metadata.filename} className="size-full object-cover" />
                        ) : (
                          <video src={i.metadata.url} className="size-full object-cover" />
                        )}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {docs.length > 0 && (
                <section>
                  <h4 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-neutral-400">
                    Documents ({docs.length})
                  </h4>
                  <ul className="space-y-1.5">
                    {docs.map((i) => (
                      <li key={i.publicId}>
                        <button
                          type="button"
                          onClick={() => setPreview(i.metadata)}
                          className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-white p-2 text-left transition-colors hover:bg-neutral-50"
                        >
                          <span className="relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-neutral-100 text-pink-600">
                            {isPdf(i.metadata) ? (
                              <iframe
                                src={`${i.metadata.url}#toolbar=0&navpanes=0&view=FitH`}
                                title={i.metadata.filename}
                                className="pointer-events-none absolute inset-0 size-full"
                                tabIndex={-1}
                              />
                            ) : (
                              <FileText className="size-5" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-neutral-800">
                              {i.metadata.filename}
                            </span>
                            <span className="block text-[11px] text-neutral-400">
                              {humanSize(i.metadata.bytes)} · {i.senderName}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>
      </div>

      {preview && <FilePreviewModal meta={preview} onClose={() => setPreview(null)} />}
    </div>,
    document.body,
  );
}
